import { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';

export interface PendingAction {
  id: string;
  label: string;
  description?: string;
  onExecute: () => Promise<void>;
  onUndo?: () => void;
}

interface UndoToastProps {
  action: PendingAction;
  delaySeconds: number;
  onComplete: (id: string, executed: boolean) => void;
}

/**
 * UndoToast - Gmail-style undo cushion for destructive actions
 * Shows a countdown timer and allows user to undo before execution
 */
export function UndoToast({ action, delaySeconds, onComplete }: UndoToastProps) {
  const [remaining, setRemaining] = useState(delaySeconds);
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(false);

  const handleUndo = useCallback(() => {
    action.onUndo?.();
    onComplete(action.id, false);
  }, [action, onComplete]);

  const executeAction = useCallback(async () => {
    if (executed || executing) return;

    setExecuting(true);
    try {
      await action.onExecute();
      setExecuted(true);
      onComplete(action.id, true);
    } catch (error) {
      console.error('Action failed:', error);
      onComplete(action.id, false);
    }
  }, [action, onComplete, executed, executing]);

  // Countdown timer
  useEffect(() => {
    if (remaining <= 0) {
      executeAction();
      return;
    }

    const timer = setInterval(() => {
      setRemaining(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [remaining, executeAction]);

  // Progress percentage for visual indicator
  const progress = ((delaySeconds - remaining) / delaySeconds) * 100;

  return (
    <div className="relative bg-surface/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-xl p-4 min-w-[300px] max-w-[400px]">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 rounded-t-xl overflow-hidden">
        <div
          className="h-full bg-accent-primary transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-warning/20 flex items-center justify-center">
          {executing ? (
            <div className="w-4 h-4 border-2 border-accent-warning border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-sm">⏳</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">
            {executing ? 'Processing...' : action.label}
          </p>
          {action.description && (
            <p className="text-xs text-white/50 mt-0.5 truncate">
              {action.description}
            </p>
          )}
          {!executing && (
            <p className="text-xs text-white/40 mt-1">
              {remaining}s remaining
            </p>
          )}
        </div>

        {/* Undo button */}
        {!executing && (
          <button
            onClick={handleUndo}
            className="flex-shrink-0 px-3 py-1.5 text-sm font-semibold text-accent-primary hover:bg-accent-primary/20 rounded-lg transition-colors"
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}

interface UndoToastContainerProps {
  pendingActions: PendingAction[];
  delaySeconds: number;
  onComplete: (id: string, executed: boolean) => void;
  onUndoAll?: () => void;
}

/**
 * Container for multiple undo toasts with batch undo option
 */
export function UndoToastContainer({
  pendingActions,
  delaySeconds,
  onComplete,
  onUndoAll,
}: UndoToastContainerProps) {
  if (pendingActions.length === 0) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 flex flex-col items-center gap-2">
      {/* Batch undo for multiple actions */}
      {pendingActions.length > 1 && onUndoAll && (
        <div className="bg-surface/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-xl p-3 flex items-center gap-3">
          <span className="text-sm text-white/70">
            {pendingActions.length} actions pending
          </span>
          <button
            onClick={onUndoAll}
            className="px-3 py-1.5 text-sm font-semibold text-accent-danger hover:bg-accent-danger/20 rounded-lg transition-colors"
          >
            Undo All
          </button>
        </div>
      )}

      {/* Individual toasts (show max 3 at a time) */}
      {pendingActions.slice(0, 3).map((action) => (
        <UndoToast
          key={action.id}
          action={action}
          delaySeconds={delaySeconds}
          onComplete={onComplete}
        />
      ))}

      {/* Overflow indicator */}
      {pendingActions.length > 3 && (
        <p className="text-xs text-white/40">
          +{pendingActions.length - 3} more pending...
        </p>
      )}
    </div>
  );
}

export default UndoToast;
